import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { 
  Building2, 
  ChevronRight, 
  Users, 
  Shield, 
  Check,
  Plus,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const SubsidiaryCreationWizard = ({ open, onClose, onSuccess, subsidiaries = [] }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    code: "",
    type: "branch", // HQ, Regional Office, Branch Office, Division, Cluster, Other
    country: "",
    city: "",
    location: "",
    address: "",
    timezone: "",
    phone: "",
    email: "",
    website: "",
    description: "",
    established_date: "",
    status: "active"
  });
  
  // Step 2: Hierarchy
  const [hierarchyInfo, setHierarchyInfo] = useState({
    parent_id: null,
    hierarchy_type: "geographic", // geographic, functional, business_unit, custom
    can_have_children: true,
    cost_center_code: "",
    budget_authority: "requires_approval", // independent, requires_approval, view_only
    reporting_currency: "USD"
  });
  const [hasChildren, setHasChildren] = useState(false);
  const [childSubsidiaries, setChildSubsidiaries] = useState([
    { name: "", code: "", location: "" }
  ]);
  
  // Step 3: Team Assignment
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [subsidiaryManager, setSubsidiaryManager] = useState(null);
  const [newTeamMembers, setNewTeamMembers] = useState([]);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [newMemberForm, setNewMemberForm] = useState({
    email: "",
    full_name: "",
    role_id: null
  });
  
  // Step 4: Role Assignment (combined with review)
  const [roles, setRoles] = useState([]);
  const [memberRoles, setMemberRoles] = useState({});
  
  useEffect(() => {
    if (open && currentStep === 3) {
      fetchCompanyUsers();
      fetchRoles();
    }
  }, [open, currentStep]);
  
  const fetchCompanyUsers = async () => {
    try {
      // Fetch company users only (not all system users)
      const response = await axios.get(`${API}/company-users`);
      const users = response.data.users || response.data || [];
      setAvailableUsers(users);
      
      // Get current user info
      const meResponse = await axios.get(`${BACKEND_URL}/api/auth/me`);
      const me = meResponse.data;
      setCurrentUser(me);
      
      // If no company users, set current user as default manager
      if (users.length === 0 && me) {
        setSubsidiaryManager(me.id);
        // Add current user to available users so they show up in the dropdown
        setAvailableUsers([{
          id: me.id,
          email: me.email,
          full_name: me.full_name || me.email,
          company_name: me.company_name
        }]);
      } else if (!subsidiaryManager && users.length > 0) {
        // Find current user in the list and set as default manager
        const currentUserInList = users.find(u => u.email === me?.email);
        if (currentUserInList) {
          setSubsidiaryManager(currentUserInList.id);
        }
      }
    } catch (error) {
      console.error("Error fetching company users:", error);
      // On error, try to at least get current user
      try {
        const meResponse = await axios.get(`${BACKEND_URL}/api/auth/me`);
        const me = meResponse.data;
        setCurrentUser(me);
        if (me) {
          setSubsidiaryManager(me.id);
          setAvailableUsers([{
            id: me.id,
            email: me.email,
            full_name: me.full_name || me.email,
            company_name: me.company_name
          }]);
        }
      } catch (e) {
        console.error("Error fetching current user:", e);
        toast.error("Failed to load team members");
      }
    }
  };
  
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles?exclude_platform=true`);
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
    }
  };
  
  const handleNext = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!basicInfo.name.trim()) {
        toast.error("Subsidiary name is required");
        return;
      }
      if (!basicInfo.code.trim()) {
        toast.error("Subsidiary code is required");
        return;
      }
    }
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleAddChildSubsidiary = () => {
    setChildSubsidiaries([...childSubsidiaries, { name: "", code: "", location: "" }]);
  };
  
  const handleRemoveChildSubsidiary = (index) => {
    setChildSubsidiaries(childSubsidiaries.filter((_, i) => i !== index));
  };
  
  const handleChildSubsidiaryChange = (index, field, value) => {
    const updated = [...childSubsidiaries];
    updated[index][field] = value;
    setChildSubsidiaries(updated);
  };
  
  const handleAddExistingMember = (userId) => {
    if (!selectedTeamMembers.includes(userId)) {
      setSelectedTeamMembers([...selectedTeamMembers, userId]);
    }
  };
  
  const handleRemoveTeamMember = (userId) => {
    setSelectedTeamMembers(selectedTeamMembers.filter(id => id !== userId));
    // Remove role assignment
    const updated = { ...memberRoles };
    delete updated[userId];
    setMemberRoles(updated);
  };
  
  const handleAddNewMember = () => {
    if (!newMemberForm.email || !newMemberForm.full_name) {
      toast.error("Email and full name are required");
      return;
    }
    
    const newMember = {
      id: `temp-${Date.now()}`,
      ...newMemberForm,
      isNew: true
    };
    
    setNewTeamMembers([...newTeamMembers, newMember]);
    setSelectedTeamMembers([...selectedTeamMembers, newMember.id]);
    setNewMemberForm({ email: "", full_name: "", role_id: null });
    setShowAddMemberForm(false);
  };
  
  const handleRoleChange = (userId, roleId) => {
    setMemberRoles({ ...memberRoles, [userId]: roleId });
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Combine basic info and hierarchy info
      const subsidiaryData = {
        ...basicInfo,
        parent_id: hierarchyInfo.parent_id,
        location: `${basicInfo.city}, ${basicInfo.country}`,
        is_active: basicInfo.status === "active"
      };
      
      // Step 1: Create main subsidiary
      const mainSubsidiaryResponse = await axios.post(`${API}/subsidiaries`, subsidiaryData);
      const mainSubsidiaryId = mainSubsidiaryResponse.data.id;
      
      toast.success("Main subsidiary created successfully");
      
      // Step 2: Create child subsidiaries if any
      if (hasChildren && childSubsidiaries.some(child => child.name && child.code)) {
        for (const child of childSubsidiaries) {
          if (child.name && child.code) {
            await axios.post(`${API}/subsidiaries`, {
              ...child,
              parent_id: mainSubsidiaryId
            });
          }
        }
        toast.success("Child subsidiaries created successfully");
      }
      
      // Step 3: Create new team members
      for (const newMember of newTeamMembers) {
        try {
          await axios.post(`${BACKEND_URL}/api/auth/register`, {
            email: newMember.email,
            full_name: newMember.full_name,
            password: "ChangeMe123!", // Temporary password
            company_name: "Default Company"
          });
        } catch (error) {
          console.error(`Failed to create user ${newMember.email}:`, error);
        }
      }
      
      // Step 4: Assign team members to subsidiary
      const allTeamMembers = [...selectedTeamMembers];
      for (const userId of allTeamMembers) {
        try {
          await axios.post(`${API}/subsidiary-members`, {
            subsidiary_id: mainSubsidiaryId,
            user_id: userId.replace('temp-', ''), // Remove temp prefix if exists
            role_id: memberRoles[userId] || null
          });
        } catch (error) {
          console.error(`Failed to assign member ${userId}:`, error);
        }
      }
      
      toast.success("Subsidiary creation completed successfully!");
      resetForm();
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error("Error creating subsidiary:", error);
      toast.error(error.response?.data?.detail || "Failed to create subsidiary");
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setCurrentStep(1);
    setBasicInfo({
      name: "",
      code: "",
      location: "",
      description: "",
      parent_id: null
    });
    setHasChildren(false);
    setChildSubsidiaries([{ name: "", code: "", location: "" }]);
    setSelectedTeamMembers([]);
    setNewTeamMembers([]);
    setMemberRoles({});
    setShowAddMemberForm(false);
  };
  
  const renderStepIndicator = () => {
    const steps = [
      { number: 1, label: "Basic Info", icon: Building2 },
      { number: 2, label: "Hierarchy", icon: Building2 },
      { number: 3, label: "Team", icon: Users },
      { number: 4, label: "Review", icon: Check }
    ];
    
    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentStep >= step.number
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {currentStep > step.number ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <step.icon className="h-5 w-5" />
                )}
              </div>
              <span className="text-xs mt-1 text-gray-600">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className="h-5 w-5 text-gray-400 mx-4 mb-5" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };
  
  const renderStep1 = () => (
    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="name">Subsidiary Name *</Label>
        <Input
          id="name"
          value={basicInfo.name}
          onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
          placeholder="e.g., Dubai International Branch"
        />
      </div>
      
      <div>
        <Label htmlFor="code">Subsidiary Code *</Label>
        <Input
          id="code"
          value={basicInfo.code}
          onChange={(e) => setBasicInfo({ ...basicInfo, code: e.target.value })}
          placeholder="e.g., DXB-004"
        />
        <p className="text-xs text-gray-500 mt-1">(Auto-generated or custom)</p>
      </div>
      
      <div>
        <Label htmlFor="type">Type *</Label>
        <Select
          value={basicInfo.type}
          onValueChange={(value) => setBasicInfo({ ...basicInfo, type: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hq">HQ</SelectItem>
            <SelectItem value="regional">Regional Office</SelectItem>
            <SelectItem value="branch">Branch Office</SelectItem>
            <SelectItem value="division">Division</SelectItem>
            <SelectItem value="cluster">Cluster</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Location Details */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="text-sm font-semibold mb-3">LOCATION DETAILS</h4>
        
        <div className="space-y-3">
          <div>
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={basicInfo.country}
              onChange={(e) => setBasicInfo({ ...basicInfo, country: e.target.value })}
              placeholder="e.g., United Arab Emirates"
            />
          </div>
          
          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={basicInfo.city}
              onChange={(e) => setBasicInfo({ ...basicInfo, city: e.target.value })}
              placeholder="e.g., Dubai"
            />
          </div>
          
          <div>
            <Label htmlFor="address">Full Address</Label>
            <Input
              id="address"
              value={basicInfo.address}
              onChange={(e) => setBasicInfo({ ...basicInfo, address: e.target.value })}
              placeholder="e.g., Sheikh Zayed Road, Dubai, UAE"
            />
          </div>
          
          <div>
            <Label htmlFor="timezone">Time Zone</Label>
            <Select
              value={basicInfo.timezone}
              onValueChange={(value) => setBasicInfo({ ...basicInfo, timezone: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timezone..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="UTC+4">Gulf Standard Time (GST) UTC+4</SelectItem>
                <SelectItem value="UTC+3">East Africa Time (EAT) UTC+3</SelectItem>
                <SelectItem value="UTC-5">Eastern Time (ET) UTC-5</SelectItem>
                <SelectItem value="UTC-8">Pacific Time (PT) UTC-8</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="text-sm font-semibold mb-3">CONTACT INFORMATION</h4>
        
        <div className="space-y-3">
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={basicInfo.phone}
              onChange={(e) => setBasicInfo({ ...basicInfo, phone: e.target.value })}
              placeholder="e.g., +971-4-XXX-XXXX"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={basicInfo.email}
              onChange={(e) => setBasicInfo({ ...basicInfo, email: e.target.value })}
              placeholder="e.g., dubai@nexora.com"
            />
          </div>
          
          <div>
            <Label htmlFor="website">Website (optional)</Label>
            <Input
              id="website"
              value={basicInfo.website}
              onChange={(e) => setBasicInfo({ ...basicInfo, website: e.target.value })}
              placeholder="e.g., https://dubai.nexora.com"
            />
          </div>
        </div>
      </div>
      
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={basicInfo.description}
          onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
          placeholder="Expansion office for Middle East operations"
          rows={3}
        />
      </div>
      
      <div>
        <Label htmlFor="established">Established Date</Label>
        <Input
          id="established"
          type="date"
          value={basicInfo.established_date}
          onChange={(e) => setBasicInfo({ ...basicInfo, established_date: e.target.value })}
        />
      </div>
      
      <div>
        <Label>Status</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={basicInfo.status === "active"}
              onChange={() => setBasicInfo({ ...basicInfo, status: "active" })}
            />
            <span>Active</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={basicInfo.status === "inactive"}
              onChange={() => setBasicInfo({ ...basicInfo, status: "inactive" })}
            />
            <span>Inactive</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={basicInfo.status === "planned"}
              onChange={() => setBasicInfo({ ...basicInfo, status: "planned" })}
            />
            <span>Planned</span>
          </label>
        </div>
      </div>
      
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            id="hasChildren"
            checked={hasChildren}
            onChange={(e) => setHasChildren(e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="hasChildren">Does this subsidiary have child subsidiaries?</Label>
        </div>
        
        {hasChildren && (
          <div className="space-y-3 pl-6">
            {childSubsidiaries.map((child, index) => (
              <div key={index} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Child Subsidiary {index + 1}</span>
                  {childSubsidiaries.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveChildSubsidiary(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                <Input
                  placeholder="Name"
                  value={child.name}
                  onChange={(e) => handleChildSubsidiaryChange(index, 'name', e.target.value)}
                />
                <Input
                  placeholder="Code"
                  value={child.code}
                  onChange={(e) => handleChildSubsidiaryChange(index, 'code', e.target.value)}
                />
                <Input
                  placeholder="Location (optional)"
                  value={child.location}
                  onChange={(e) => handleChildSubsidiaryChange(index, 'location', e.target.value)}
                />
              </div>
            ))}
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddChildSubsidiary}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Child Subsidiary
            </Button>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="parent">Parent Subsidiary (Optional)</Label>
        <Select
          value={hierarchyInfo.parent_id || "none"}
          onValueChange={(value) => setHierarchyInfo({ ...hierarchyInfo, parent_id: value === "none" ? null : value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select parent subsidiary..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None (Top Level)</SelectItem>
            {subsidiaries.filter(s => s.is_active).map((sub) => (
              <SelectItem key={sub.id} value={sub.id}>
                {sub.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">(Leave empty if this is a top-level subsidiary)</p>
      </div>

      {/* Hierarchy Preview */}
      {hierarchyInfo.parent_id && (
        <div className="border rounded-lg p-4 bg-blue-50">
          <h4 className="text-sm font-semibold mb-3">CURRENT HIERARCHY PREVIEW</h4>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <span>Nexora (HQ)</span>
            </div>
            {subsidiaries.find(s => s.id === hierarchyInfo.parent_id) && (
              <div className="flex items-center gap-2 ml-4">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span>{subsidiaries.find(s => s.id === hierarchyInfo.parent_id)?.name} (Parent)</span>
              </div>
            )}
            <div className="flex items-center gap-2 ml-8">
              <Building2 className="h-4 w-4 text-blue-500" />
              <span className="font-medium text-blue-700">{basicInfo.name} (New) ← You are here</span>
            </div>
          </div>
        </div>
      )}

      <div>
        <Label>Hierarchy Type</Label>
        <div className="space-y-2 mt-2">
          <label className="flex items-start gap-2">
            <input
              type="radio"
              checked={hierarchyInfo.hierarchy_type === "geographic"}
              onChange={() => setHierarchyInfo({ ...hierarchyInfo, hierarchy_type: "geographic" })}
              className="mt-1"
            />
            <div>
              <div className="font-medium">Geographic (Region → Country → City)</div>
              <div className="text-xs text-gray-500">Organized by geographical location</div>
            </div>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              checked={hierarchyInfo.hierarchy_type === "functional"}
              onChange={() => setHierarchyInfo({ ...hierarchyInfo, hierarchy_type: "functional" })}
              className="mt-1"
            />
            <div>
              <div className="font-medium">Functional (Department → Division → Team)</div>
              <div className="text-xs text-gray-500">Organized by business function</div>
            </div>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              checked={hierarchyInfo.hierarchy_type === "business_unit"}
              onChange={() => setHierarchyInfo({ ...hierarchyInfo, hierarchy_type: "business_unit" })}
              className="mt-1"
            />
            <div>
              <div className="font-medium">Business Unit (Product Line → Market → Customer Segment)</div>
              <div className="text-xs text-gray-500">Organized by product or market</div>
            </div>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              checked={hierarchyInfo.hierarchy_type === "custom"}
              onChange={() => setHierarchyInfo({ ...hierarchyInfo, hierarchy_type: "custom" })}
              className="mt-1"
            />
            <div>
              <div className="font-medium">Custom</div>
              <div className="text-xs text-gray-500">Define your own structure</div>
            </div>
          </label>
        </div>
      </div>

      <div>
        <Label>Can have child subsidiaries?</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hierarchyInfo.can_have_children}
              onChange={(e) => setHierarchyInfo({ ...hierarchyInfo, can_have_children: e.target.checked })}
            />
            <span className="text-sm">Yes (e.g., this branch can create sub-branches)</span>
          </label>
        </div>
      </div>

      {/* Operational Settings */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="text-sm font-semibold mb-3">OPERATIONAL SETTINGS</h4>
        
        <div className="space-y-3">
          <div>
            <Label htmlFor="cost_center">Cost Center Code</Label>
            <Input
              id="cost_center"
              value={hierarchyInfo.cost_center_code}
              onChange={(e) => setHierarchyInfo({ ...hierarchyInfo, cost_center_code: e.target.value })}
              placeholder="e.g., CC-DXB-004"
            />
          </div>
          
          <div>
            <Label htmlFor="budget_authority">Budget Authority Level</Label>
            <Select
              value={hierarchyInfo.budget_authority}
              onValueChange={(value) => setHierarchyInfo({ ...hierarchyInfo, budget_authority: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="independent">Independent</SelectItem>
                <SelectItem value="requires_approval">Requires Approval</SelectItem>
                <SelectItem value="view_only">View Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="currency">Reporting Currency</Label>
            <Select
              value={hierarchyInfo.reporting_currency}
              onValueChange={(value) => setHierarchyInfo({ ...hierarchyInfo, reporting_currency: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD (US Dollar)</SelectItem>
                <SelectItem value="EUR">EUR (Euro)</SelectItem>
                <SelectItem value="GBP">GBP (British Pound)</SelectItem>
                <SelectItem value="AED">AED (UAE Dirham)</SelectItem>
                <SelectItem value="ETB">ETB (Ethiopian Birr)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Info banner when only current user is available */}
      {availableUsers.length <= 1 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> No other team members found in your company. 
            You have been set as the default subsidiary manager. You can add new team members below.
          </p>
        </div>
      )}
      
      <div>
        <Label>Subsidiary Manager (Required)</Label>
        <Select
          value={subsidiaryManager || "none"}
          onValueChange={(value) => setSubsidiaryManager(value === "none" ? null : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select manager..." />
          </SelectTrigger>
          <SelectContent>
            {availableUsers.length === 0 && (
              <SelectItem value="none">No users available</SelectItem>
            )}
            {availableUsers.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.full_name || user.email} {currentUser?.id === user.id ? "(You)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {subsidiaryManager && (
          <div className="mt-2 p-2 bg-blue-50 rounded flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-sm">
              ✓ Selected: {availableUsers.find(u => u.id === subsidiaryManager)?.full_name || 
                          availableUsers.find(u => u.id === subsidiaryManager)?.email}
              {currentUser?.id === subsidiaryManager ? " (You)" : ""}
            </span>
            <Badge className="ml-auto">Subsidiary Manager</Badge>
          </div>
        )}
      </div>

      <div>
        <Label>Assign Existing Team Members</Label>
        {availableUsers.filter(user => !selectedTeamMembers.includes(user.id) && user.id !== subsidiaryManager).length === 0 ? (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
            No additional team members available. Use "Add New Team Member" below to invite people.
          </div>
        ) : (
          <Select onValueChange={handleAddExistingMember}>
            <SelectTrigger>
              <SelectValue placeholder="Select team member..." />
            </SelectTrigger>
            <SelectContent>
              {availableUsers
                .filter(user => !selectedTeamMembers.includes(user.id) && user.id !== subsidiaryManager)
                .map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.full_name || user.email} {currentUser?.id === user.id ? "(You)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      {!showAddMemberForm && (
        <Button
          variant="outline"
          onClick={() => setShowAddMemberForm(true)}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Team Member
        </Button>
      )}
      
      {showAddMemberForm && (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">New Team Member</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowAddMemberForm(false);
                setNewMemberForm({ email: "", full_name: "", role_id: null });
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={newMemberForm.email}
              onChange={(e) => setNewMemberForm({ ...newMemberForm, email: e.target.value })}
              placeholder="user@example.com"
            />
          </div>
          
          <div>
            <Label>Full Name *</Label>
            <Input
              value={newMemberForm.full_name}
              onChange={(e) => setNewMemberForm({ ...newMemberForm, full_name: e.target.value })}
              placeholder="John Doe"
            />
          </div>
          
          <Button onClick={handleAddNewMember} className="w-full">
            Add Member
          </Button>
        </div>
      )}
      
      {selectedTeamMembers.length > 0 && (
        <div className="border rounded-lg p-4">
          <Label className="mb-3 block">Selected Team Members ({selectedTeamMembers.length})</Label>
          <div className="space-y-2">
            {selectedTeamMembers.map((userId) => {
              const user = availableUsers.find(u => u.id === userId) || 
                           newTeamMembers.find(u => u.id === userId);
              
              return (
                <div key={userId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">
                    {user?.full_name || user?.email || userId}
                    {user?.isNew && <Badge variant="secondary" className="ml-2 text-xs">New</Badge>}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveTeamMember(userId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
  
  const renderStep4 = () => {
    const parentSub = subsidiaries.find(s => s.id === hierarchyInfo.parent_id);
    const manager = availableUsers.find(u => u.id === subsidiaryManager);
    
    return (
      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
        <p className="text-sm text-gray-600">Please review all information before creating the subsidiary:</p>
        
        {/* Basic Information Review */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">✅ Basic Information</h4>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setCurrentStep(1)}
            >
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <span className="font-medium ml-2">{basicInfo.name}</span>
            </div>
            <div>
              <span className="text-gray-600">Code:</span>
              <span className="font-medium ml-2">{basicInfo.code}</span>
            </div>
            <div>
              <span className="text-gray-600">Type:</span>
              <span className="font-medium ml-2 capitalize">{basicInfo.type}</span>
            </div>
            <div>
              <span className="text-gray-600">Location:</span>
              <span className="font-medium ml-2">{basicInfo.city}, {basicInfo.country}</span>
            </div>
          </div>
        </div>

        {/* Hierarchy Review */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">✅ Hierarchy</h4>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setCurrentStep(2)}
            >
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600">Parent:</span>
              <span className="font-medium ml-2">{parentSub?.name || "None (Top Level)"}</span>
            </div>
            <div>
              <span className="text-gray-600">Type:</span>
              <span className="font-medium ml-2 capitalize">{hierarchyInfo.hierarchy_type.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-gray-600">Cost Center:</span>
              <span className="font-medium ml-2">{hierarchyInfo.cost_center_code || "N/A"}</span>
            </div>
            <div>
              <span className="text-gray-600">Currency:</span>
              <span className="font-medium ml-2">{hierarchyInfo.reporting_currency}</span>
            </div>
          </div>
        </div>

        {/* Team Review */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">
              ✅ Team ({selectedTeamMembers.length + (subsidiaryManager ? 1 : 0)} members)
            </h4>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setCurrentStep(3)}
            >
              Edit
            </Button>
          </div>
          <div className="space-y-2">
            {subsidiaryManager && (
              <div className="text-sm">
                <span className="text-gray-600">Manager:</span>
                <span className="font-medium ml-2">{manager?.full_name || manager?.email}</span>
              </div>
            )}
            {selectedTeamMembers.length > 0 && (
              <div className="text-sm">
                <span className="text-gray-600">Team Members:</span>
                <div className="ml-2 mt-1 space-y-1">
                  {selectedTeamMembers.slice(0, 3).map((userId) => {
                    const user = availableUsers.find(u => u.id === userId) || 
                                 newTeamMembers.find(u => u.id === userId);
                    return (
                      <div key={userId} className="font-medium">
                        • {user?.full_name || user?.email}
                      </div>
                    );
                  })}
                  {selectedTeamMembers.length > 3 && (
                    <div className="text-gray-500">
                      ... and {selectedTeamMembers.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Options */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" defaultChecked />
              <span>Send welcome email to team members</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" />
              <span>Create default business plan template</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" />
              <span>Schedule onboarding session</span>
            </label>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Subsidiary</DialogTitle>
        </DialogHeader>
        
        {renderStepIndicator()}
        
        <div className="py-4">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || loading}
          >
            Back
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            
            {currentStep < 4 ? (
              <Button onClick={handleNext} disabled={loading}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? "Creating..." : "Create Subsidiary"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubsidiaryCreationWizard;
